import { forwardRef, Fragment, useCallback, useEffect, useImperativeHandle, useRef, useState } from 'react'
import { InlineSpinner } from 'shared/components/Spinner'
import { HandlerOf } from 'shared/helpers/typeHelper'

import { KEYBOARD } from '../../helpers/constants'
import { Colors } from '../../theme/colors'
import { TYPOGRAPHY } from '../../theme/typographyV2'
import Overlay from '../Overlay/Overlay'
import { TextV2 } from '../Text/TextV2'
import { TextInput } from '../TextInput/TextInput'
import { DropdownMonoselectProps, DropdownMultiselectProps, DropdownOption } from './Dropdown'
import { DropdownOpenerContainer, EmptyOption, Option, Options, OptionsBottomAction } from './Dropdown.styles'

declare module 'react' {
  function forwardRef<T, P = {}>(
    render: (props: P, ref: React.Ref<T>) => React.ReactElement | null
  ): (props: P & React.RefAttributes<T>) => React.ReactElement | null
}

interface DropDownOpenerMonoselectProps<T> extends Omit<DropdownMonoselectProps<T>, 'children'> {}

interface DropDownOpenerMultiselectProps<T> extends Omit<DropdownMultiselectProps<T>, 'children'> {}

export type DropDownOpenerProps<T> = (
  | DropDownOpenerMonoselectProps<T>
  | DropDownOpenerMultiselectProps<T>
) & {
  children: (isOpen: boolean, setOpen: HandlerOf<boolean>, multiselectSelectedValues: T[]) => React.ReactNode
  bottomAction?: React.ReactNode
}

function DropDownOpenerInner<T>(
  {
    className,
    options,
    isInitiallyOpen = false,
    isExternalOpen = undefined,
    closeOnClick = true,
    children,
    bottomAction,
    maxHeight,
    bottom,
    width,
    align,
    positionX,
    positionY,
    optionsLabel,
    optionsMargin,
    emptyOptionsLabel,
    isDisabled,
    noOverlay,
    onChange,
    onBlur,
    onToggle,
    value: valueProp,
    fontSize,
    addOptionsFilter,
    isMultiselect = false,
    isChangeOnBlurMultiselect = false,
    isLoading = false,
    customZIndex,
    scrollToSelectedValue,
    selectedValueDataAttribute,
  }: DropDownOpenerProps<T>,
  ref: any
) {
  const [optionFilterValue, setOptionFilterValue] = useState('')
  const [isOpen, setOpen] = useState(isInitiallyOpen)
  const [multiselectSelectedValues, setMultiselectSelectedValues] = useState<T[]>(
    isMultiselect || isChangeOnBlurMultiselect ? ((valueProp ?? []) as T[]) : []
  )
  const value = isMultiselect && isChangeOnBlurMultiselect ? multiselectSelectedValues : valueProp

  const wrap = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (isMultiselect && isChangeOnBlurMultiselect && valueProp && Array.isArray(valueProp)) {
      setMultiselectSelectedValues(valueProp as T[])
    }
  }, [isMultiselect, isChangeOnBlurMultiselect, valueProp])

  useImperativeHandle(ref, () => ({
    openDropdown: () => setOpen(true),
    closeDropdown: () => setOpen(false),
    focus: () => wrap.current?.focus(),
  }))

  const handleToggleOptions = (e: React.MouseEvent) => {
    e.stopPropagation()
    if (isDisabled) return
    onToggle?.(!isOpen)
    setOpen((prevState) => !prevState)
  }

  const handleOverlayClick = (e: React.MouseEvent) => {
    e.stopPropagation()
    onBlur?.()
    setOpen(false)

    if (isMultiselect && isChangeOnBlurMultiselect) {
      const multiselectValue: T[] = (value ?? []) as T[]
      const multiselectOnChange = onChange as HandlerOf<T[]> | undefined
      multiselectOnChange?.(multiselectValue)
    }
  }

  const handleOnClick = (option: DropdownOption<T>, e: React.MouseEvent) => {
    !isChangeOnBlurMultiselect && closeOnClick && setOpen(false)
    if (isMultiselect && isChangeOnBlurMultiselect) {
      e.stopPropagation()
      e.preventDefault()
      return setMultiselectSelectedValues((currValues) =>
        currValues.includes(option.value as T)
          ? currValues.filter((v) => v !== option.value)
          : currValues.concat(option.value as T)
      )
    } else if (isMultiselect) {
      const multiselectValue = (value ?? []) as T[]
      const multiselectOnChange = onChange as HandlerOf<T[]> | undefined
      option.value &&
        (multiselectValue.includes(option.value)
          ? multiselectOnChange?.(multiselectValue.filter((v) => v !== option.value))
          : multiselectOnChange?.(multiselectValue.concat(option.value)))
    } else {
      const monoselectOnChange = onChange as HandlerOf<T> | undefined
      option.value && monoselectOnChange?.(option.value as T)
    }
    option.onClick && option.onClick()
  }

  const handleKeyDown = useCallback((event) => {
    if (event.key === KEYBOARD.ESCAPE) setOpen(false)
  }, [])

  useEffect(() => {
    document.addEventListener('keydown', handleKeyDown)
    return () => document.removeEventListener('keydown', handleKeyDown)
  }, [handleKeyDown])

  useEffect(() => {
    if (isExternalOpen === true && !isOpen) setOpen(true)
    else if (isExternalOpen === false && isOpen) setOpen(false)
  }, [isExternalOpen, isOpen])

  // Scroll to selected value when dropdown opens
  useEffect(() => {
    if (isOpen && scrollToSelectedValue && selectedValueDataAttribute && value) {
      // Use setTimeout to ensure DOM is rendered
      setTimeout(() => {
        const optionsContainer = wrap.current?.querySelector('.dropdown-opener-options') as HTMLElement
        if (!optionsContainer) return

        const selectedOption = optionsContainer.querySelector(
          `[${selectedValueDataAttribute}="${value}"]`
        ) as HTMLElement
        if (selectedOption) {
          const containerRect = optionsContainer.getBoundingClientRect()
          const optionRect = selectedOption.getBoundingClientRect()
          const scrollTop = optionsContainer.scrollTop
          const optionOffset = optionRect.top - containerRect.top + scrollTop
          optionsContainer.scrollTop = optionOffset - containerRect.height / 2 + optionRect.height / 2
        }
      }, 50)
    }
  }, [isOpen, scrollToSelectedValue, selectedValueDataAttribute, value])

  function renderOption(option: DropdownOption<T>) {
    const multiselectValue = (value ?? []) as T[]
    const isSelected = isMultiselect
      ? option.value && multiselectValue && multiselectValue.includes(option.value)
      : value && option.value === value
    return (
      <Option
        key={String(option.value || option.label)}
        onClick={(e) => handleOnClick(option, e)}
        data-select-option={option.label}
        $isSelected={isSelected}
      >
        {option.dropDownLabel || option.label}
      </Option>
    )
  }

  const isOpenWithOptions =
    isOpen && (options.length || emptyOptionsLabel || optionsLabel || bottomAction || isLoading)
  const lowercasedFilter = optionFilterValue?.toLowerCase()
  const filteredOptions =
    optionFilterValue && addOptionsFilter
      ? options?.filter((o) => {
          if (typeof o.label === 'string' && o.label.toLowerCase().includes(lowercasedFilter)) return true
          if (typeof o.value === 'string' && o.value.toLowerCase().includes(lowercasedFilter)) return true
          return false
        })
      : options

  return (
    <DropdownOpenerContainer data-testid="dropdown-opener-container" className={className} ref={wrap}>
      <div onClick={handleToggleOptions}>{children(isOpen, setOpen, multiselectSelectedValues)}</div>
      {isOpenWithOptions && (
        <>
          {noOverlay ? null : <Overlay onClick={handleOverlayClick} isTransparent={true} />}
          <Options
            data-testid="dropdown-opener-options"
            className="dropdown-opener-options"
            $bottom={bottom}
            $width={width}
            $align={align}
            $positionX={positionX}
            $positionY={positionY}
            $maxHeight={maxHeight}
            $margin={optionsMargin}
            $fontSize={fontSize}
            $customZIndex={customZIndex}
          >
            {optionsLabel ? (
              <EmptyOption>
                <TextV2 type={TYPOGRAPHY.header4} className="text-mid-gray">
                  {optionsLabel}
                </TextV2>
              </EmptyOption>
            ) : null}
            {addOptionsFilter ? (
              <EmptyOption>
                <TextInput
                  value={optionFilterValue}
                  onChange={(e) => setOptionFilterValue(e.target.value)}
                  backgroundColor={Colors.bgGray}
                  label="Filter"
                  noMargin
                  isSmall
                />
              </EmptyOption>
            ) : null}
            {filteredOptions.length === 0 && emptyOptionsLabel ? (
              <EmptyOption>{emptyOptionsLabel}</EmptyOption>
            ) : null}
            {filteredOptions.map((option: DropdownOption<T>) => {
              return (
                <Fragment key={option.value! as string}>
                  {option.renderer ? option.renderer(option, () => setOpen(false)) : renderOption(option)}
                </Fragment>
              )
            })}
            {isLoading && <InlineSpinner />}
          </Options>
          {bottomAction && (
            <OptionsBottomAction
              $maxHeight={maxHeight}
              $width={width}
              $align={align}
              $customZIndex={customZIndex}
            >
              {bottomAction}
            </OptionsBottomAction>
          )}
        </>
      )}
    </DropdownOpenerContainer>
  )
}

export const DropDownOpener = forwardRef(DropDownOpenerInner)
